import {
  faArrowLeft,
  faCupTogo,
  faPencil
} from "@fortawesome/pro-light-svg-icons";
import { Controller, useForm } from "react-hook-form";
import { Link, Navigate, Outlet } from "react-router-dom";

import { priceFormatter } from "~/utils/price-formatter";
import { AllowanceChip } from "~/components";
import { useVatLabel } from "~/hooks/use-vat-label";
import { T, useT } from "@repo/transifex";
import {
  Basket,
  BasketBody,
  BasketFooter,
  BasketHeader,
  BasketProducts,
  BasketSumUp,
  OrderInstructions
} from "~/components/kiosk/basket";
import {
  OrderInstructionInput,
  OrderInstructionItem,
  OrderInstructionSelect
} from "~/components/kiosk/basket/order-instruction-item/order-instruction-item";
import { ProductItemLine } from "~/components/kiosk/basket/product-item-line/product-item-line";
import {
  BottomActions,
  SecondaryButton
} from "~/components/kiosk/bottom-actions/bottom-actions";
import { KioskPaymentButtons } from "~/components/kiosk/payment-buttons/payment-buttons";
import { UserInfo } from "~/components/kiosk/products/user-info";
import { kioskActions } from "~/stores/kiosk-session/actions";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { useUserProductsKiosk } from "~/api/products/hooks/use-user-products";
import { resolveBasketTaxTotals } from "~/lib/basket-tax-totals";
import { resolveDisplayDepositPrices } from "~/stores/kiosk-session/hooks/use-kiosk-products/resolve-display-deposit-prices";
import { resolveDisplayLineTotals } from "~/stores/kiosk-session/hooks/use-kiosk-products/resolve-display-totals";
import {
  useKioskDiningOption,
  useKioskSession,
  useKioskSessionLocale
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { getDiningOptionsInput } from "~/utils/get-dining-options-input";
import {
  kioskPostCheckoutTextInputs,
  kioskRequiresOrderInstructions,
  kioskUsesCustomShopperName
} from "~/utils/kiosk-requires-order-instructions";

import { useKioskStore } from "../__app.kiosk/route";
import { useKioskShopperName } from "../__app.kiosk/use-kiosk-shopper-name";

export const KioskBasketPage = () => {
  const till = useKioskStore((state) => state.till);
  const user = useKioskStore((state) => state.user);
  const hidePrices = till.moduleConfig.hidePrices;
  const { data: userProducts } = useUserProductsKiosk();
  const userGroupName = userProducts?.user?.groupName;

  const locale = useKioskSessionLocale();
  const diningOption = useKioskDiningOption();
  const shopperName = useKioskShopperName();
  const postCheckoutTextInputs = useKioskSession(
    (session) => session?.postCheckoutTextInputs
  );

  const orderId = useKioskSession((session) => session?.orderId);

  if (!orderId) {
    throw new Error("Order ID is not available");
  }

  const {
    productsBasket,
    productsList,
    quantitySum,
    unitTotalPriceInclVat: unitTotalPriceInclVatTotal,
    unitTotalDepositInclVat,
    unitTotalDepositExclVat,
    unitTotalPayableInclVat,
    unitTotalAllowanceDeductionInclVat: allowanceDeduction,
    showPricesInclVat,
    currency,
    allowances,
    discounts
  } = useKioskProducts();

  const { vatLabel } = useVatLabel();

  const t = useT();

  const showCustomShopperName = kioskUsesCustomShopperName(till);

  const visiblePostCheckoutTextInputs = kioskPostCheckoutTextInputs(
    till,
    userGroupName
  );

  const { control, trigger: triggerInstructionsValidation } = useForm<{
    shopperName: string;
    postCheckoutTextInputs: Record<string, string>;
  }>({
    mode: "onBlur",
    // Mirrors what the order payload reads, so identifying on this screen fills
    // the name in without waiting for a remount.
    values: {
      shopperName: shopperName ?? "",
      postCheckoutTextInputs: Object.fromEntries(
        visiblePostCheckoutTextInputs.map((input) => [
          input.key,
          postCheckoutTextInputs?.[input.key] ?? ""
        ])
      )
    },
    resetOptions: { keepDirtyValues: true }
  });

  if (!productsBasket.length) {
    return <Navigate to="/kiosk/products" />;
  }

  const taxTotals = resolveBasketTaxTotals({
    showPricesInclVat,
    items: productsBasket
  });

  const formattedTotalAmount = priceFormatter({
    value: unitTotalPayableInclVat,
    currency,
    locale
  });

  const depositTotal = showPricesInclVat
    ? unitTotalDepositInclVat
    : unitTotalDepositExclVat;

  const depositSummary =
    depositTotal > 0
      ? priceFormatter({
          value: depositTotal,
          currency,
          locale
        })
      : undefined;

  // Products only, so the column reads subtotal − allowance + deposit = total.
  // A tax breakdown closes the block this row lives in and moves the deposit
  // below *Subtotal excl.*, so only an allowance still carries it there.
  const subtotalSummary =
    allowanceDeduction > 0 || (depositSummary !== undefined && !taxTotals)
      ? priceFormatter({
          value: unitTotalPriceInclVatTotal + allowanceDeduction,
          currency,
          locale
        })
      : undefined;

  const allowanceSummary =
    allowanceDeduction > 0
      ? {
          applied: priceFormatter({
            value: -allowanceDeduction,
            currency,
            locale
          }),
          productCount: productsBasket.reduce(
            (acc, item) =>
              item.appliedAllowanceType === "numberOfProducts"
                ? acc + item.quantity
                : acc,
            0
          )
        }
      : undefined;

  const taxSummary = taxTotals
    ? {
        label: vatLabel,
        totalExclVat: priceFormatter({
          value: taxTotals.totalExclVat,
          currency,
          locale
        }),
        lines: taxTotals.taxLines.map((line) => ({
          rateLabel: `${(line.rate * 100).toFixed(2)}%`,
          taxAmount: priceFormatter({
            value: line.taxAmount,
            currency,
            locale
          })
        }))
      }
    : undefined;

  const diningOptionsInput = getDiningOptionsInput(till.preCheckoutTextInputs);

  const showOrderInstructionsForm = kioskRequiresOrderInstructions(
    till,
    userGroupName
  );

  return (
    <div className="h-screen scroll-pb-80 overflow-y-scroll">
      <Basket>
        <BasketHeader>
          {user ? (
            <div className="flex items-center gap-6">
              <AllowanceChip
                allowances={allowances}
                discounts={discounts}
                currency={currency}
                locale={locale}
              />
              <UserInfo userName={user.name} />
            </div>
          ) : null}
        </BasketHeader>

        <BasketBody>
          <BasketProducts>
            {productsBasket.map(
              ({
                product,
                unitTotalPriceInclVat,
                unitTotalListPriceInclVat,
                unitTotalPriceExclVat: lineTotalExclVat,
                unitTotalListPriceExclVat: lineTotalListExclVat,
                productDepositTotalInclVat,
                productDepositTotalExclVat,
                variantDepositTotals,
                quantity,
                basketItemId,
                isRedeemed,
                appliedAllowanceType
              }) => {
                const { depositPrice, variantDepositPrices } =
                  resolveDisplayDepositPrices({
                    showPricesInclVat,
                    hidePrices,
                    currency,
                    locale,
                    productDepositTotalInclVat,
                    productDepositTotalExclVat,
                    variantDepositTotals
                  });

                const { displayLineTotal, displayLineListTotal } =
                  resolveDisplayLineTotals({
                    showPricesInclVat,
                    unitTotalPriceInclVat,
                    unitTotalListPriceInclVat,
                    unitTotalPriceExclVat: lineTotalExclVat,
                    unitTotalListPriceExclVat: lineTotalListExclVat
                  });

                const handleQuantityIncrease = () => {
                  if (appliedAllowanceType !== "numberOfProducts") {
                    kioskActions.basket.increment(basketItemId);
                    return;
                  }

                  const listEntry = productsList.find(
                    (entry) => entry.product.id === product.id
                  );

                  kioskActions.basket.increment(
                    basketItemId,
                    listEntry?.allowance?.type === "numberOfProducts"
                      ? listEntry.allowance.remaining
                      : undefined
                  );
                };

                return (
                  <ProductItemLine
                    choices={product.type === "variant" ? product.choices : []}
                    weight={
                      product.type === "weighted"
                        ? {
                            weight: product.weight,
                            unit: product.weightUnit,
                            decimals: product.weightDecimals
                          }
                        : null
                    }
                    key={basketItemId}
                    imageUrl={product.imageUrl}
                    name={product.name}
                    displayPrice={
                      hidePrices
                        ? null
                        : priceFormatter({
                            value: displayLineTotal,
                            currency,
                            locale
                          })
                    }
                    displayPriceOriginal={
                      displayLineListTotal && !hidePrices
                        ? priceFormatter({
                            value: displayLineListTotal,
                            currency,
                            locale
                          })
                        : null
                    }
                    depositPrice={depositPrice}
                    variantDepositPrices={variantDepositPrices}
                    quantity={quantity}
                    stamps={product.stamps}
                    isRedeemed={isRedeemed}
                    appliedAllowanceType={appliedAllowanceType}
                    incrementDisabled={
                      product.disabled || product.type === "weighted"
                    }
                    onQuantityIncrease={handleQuantityIncrease}
                    onQuantityDecrease={() => {
                      kioskActions.basket.decrement(basketItemId);
                    }}
                  />
                );
              }
            )}
          </BasketProducts>

          {hidePrices ? null : (
            <BasketSumUp
              itemCount={quantitySum}
              totalAmount={formattedTotalAmount}
              subtotal={subtotalSummary}
              allowance={allowanceSummary}
              deposit={depositSummary}
              tax={taxSummary}
            />
          )}
        </BasketBody>

        {showOrderInstructionsForm ? (
          <OrderInstructions>
            {/* The dining option is not managed by react-hook-form.
              Selecting a value updates the kiosk store via an action.
            */}
            {diningOptionsInput ? (
              <OrderInstructionItem icon={faCupTogo} name={t("Dining option")}>
                <OrderInstructionSelect
                  options={diningOptionsInput.options.map((option) => ({
                    value: option,
                    label: option
                  }))}
                  value={
                    diningOption?.value
                      ? {
                          value: diningOption.value,
                          label: diningOption.value
                        }
                      : null
                  }
                  onChange={(e) => {
                    if (e) {
                      kioskActions.session.setDiningOption({
                        inputId: diningOptionsInput.inputId,
                        value: e.value
                      });
                    }
                  }}
                />
              </OrderInstructionItem>
            ) : null}

            {showCustomShopperName ? (
              <Controller
                control={control}
                name="shopperName"
                rules={{
                  required:
                    till.config.customShopperName === "required"
                      ? t("Name is required")
                      : false
                }}
                render={({ field, fieldState }) => (
                  <OrderInstructionItem
                    icon={faPencil}
                    name={t("Name")}
                    required={till.config.customShopperName === "required"}
                    errorMessage={fieldState.error?.message}
                  >
                    <OrderInstructionInput
                      {...field}
                      required={till.config.customShopperName === "required"}
                      placeholder={
                        till.config.customShopperName === "required"
                          ? t("Required")
                          : t("Optional")
                      }
                      onBlur={(e) => {
                        field.onBlur();
                        kioskActions.session.setShopperName(e.target.value);
                      }}
                    />
                  </OrderInstructionItem>
                )}
              />
            ) : null}

            {visiblePostCheckoutTextInputs.length
              ? visiblePostCheckoutTextInputs.map((input) => (
                  <Controller
                    control={control}
                    key={input.key}
                    name={`postCheckoutTextInputs.${input.key}`}
                    rules={{
                      required: input.required ? t("Input is required") : false
                    }}
                    render={({ field, fieldState }) => (
                      <OrderInstructionItem
                        icon={faPencil}
                        name={input.title}
                        required={input.required}
                        errorMessage={fieldState.error?.message}
                      >
                        <OrderInstructionInput
                          {...field}
                          required={input.required}
                          placeholder={
                            input.required ? t("Required") : t("Optional")
                          }
                          onBlur={(e) => {
                            field.onBlur();
                            kioskActions.session.setPostCheckoutTextInput(
                              input.key,
                              e.target.value
                            );
                          }}
                        />
                      </OrderInstructionItem>
                    )}
                  />
                ))
              : null}
          </OrderInstructions>
        ) : null}

        <BasketFooter>
          <BottomActions
            secondaryButton={
              <Link to="/kiosk/products" className="flex flex-1">
                <SecondaryButton
                  icon={faArrowLeft}
                  className="flex-col gap-y-5"
                >
                  <T _str="Back" />
                </SecondaryButton>
              </Link>
            }
            primaryButton={
              <KioskPaymentButtons
                disabled={productsBasket.length === 0}
                onBeforeSubmit={() =>
                  triggerInstructionsValidation(undefined, {
                    shouldFocus: true
                  })
                }
              />
            }
          />
        </BasketFooter>
      </Basket>

      <Outlet />
    </div>
  );
};
